import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox as MuiCheckbox,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { com, kotlin } from '@eidu/form'
import { RenderData } from '../assetTypes'

import Page = com.eidu.sharedlib.form.definition.Page
import Item = com.eidu.sharedlib.form.definition.Item
import Checkbox = com.eidu.sharedlib.form.definition.Checkbox
import SingleLine = com.eidu.sharedlib.form.definition.SingleLine
import MultiLine = com.eidu.sharedlib.form.definition.MultiLine
import SingleChoice = com.eidu.sharedlib.form.definition.SingleChoice
import MultipleChoice = com.eidu.sharedlib.form.definition.MultipleChoice
import MultiAnswer = com.eidu.sharedlib.form.definition.MultiAnswer
import Description = com.eidu.sharedlib.form.definition.Description
import Section = com.eidu.sharedlib.form.definition.Section
import DateItem = com.eidu.sharedlib.form.definition.Date
import LocationItem = com.eidu.sharedlib.form.definition.Location
import GradeDropDown = com.eidu.sharedlib.form.definition.GradeDropDown
import StreamDropDown = com.eidu.sharedlib.form.definition.StreamDropDown
import EntityChoice = com.eidu.sharedlib.form.definition.EntityChoice
import SchoolDropDown = com.eidu.sharedlib.form.definition.SchoolDropDown
import UserDropDown = com.eidu.sharedlib.form.definition.UserDropDown
import InputType = com.eidu.sharedlib.form.definition.InputType
import SingleChoiceStyle = com.eidu.sharedlib.form.definition.SingleChoiceStyle
import Option = com.eidu.sharedlib.form.definition.Option
import Next = com.eidu.sharedlib.form.definition.Next
import NextItem = com.eidu.sharedlib.form.definition.NextItem
import NextPage = com.eidu.sharedlib.form.definition.NextPage
import EntitySelector = com.eidu.sharedlib.entity.selector.EntitySelector
import EntityType = com.eidu.sharedlib.entity.type.EntityType
import Entity = com.eidu.sharedlib.entity.Entity
import FieldId = com.eidu.sharedlib.entity.field.FieldId
import FieldValue = com.eidu.sharedlib.entity.field.FieldValue
import ReferenceValue = com.eidu.sharedlib.entity.field.ReferenceValue
import entityTypePaginatedResponseFromJsonString = com.eidu.sharedlib.entity.api.types.entityTypePaginatedResponseFromJsonString
import entityPaginatedResponseFromJsonString = com.eidu.sharedlib.entity.api.entities.entityPaginatedResponseFromJsonString
import getPrimaryText = com.eidu.sharedlib.entity.label.getPrimaryText
import decodeFormContentFromYaml = com.eidu.sharedlib.form.decodeFormContentFromYaml
import PhoneNumber = com.eidu.sharedlib.user.PhoneNumber
import isRealMobilePhoneNumber = com.eidu.sharedlib.user.isRealMobilePhoneNumber
import KtList = kotlin.collections.KtList

type FormPreviewProps = {
  content: string
  renderData?: RenderData
  previewWidth?: number
  previewHeight?: number
  reloadToken?: number
  previewSx?: SxProps<Theme>
  layout?: 'fixed' | 'fill'
  showLog?: boolean
  onLog?: (message: string) => void
}

type EntityOption = {
  id: string
  typeId: string
  label: string
}

type FormOption = {
  id: string
  label: string
  value: string
  next?: Next | null
}

type FormInputDefinition = {
  kind: 'text' | 'textarea' | 'checkbox' | 'radio' | 'dropdown' | 'date' | 'location' | 'entity'
  key: string
  label?: string
  required: boolean
  placeholder?: string
  inputType?: InputType
  options?: FormOption[]
  defaultValue?: string
  unfilledText?: string
  entitySelector?: EntitySelector
  entityStyle?: 'dropdown' | 'radio'
}

type ConditionalBranch = {
  id: string
  matchValue: string | boolean
  element: FormElementDefinition
}

type FormElementDefinition =
  | { kind: 'input'; key: string; input: FormInputDefinition }
  | {
      kind: 'composite'
      key: string
      label?: string
      required: boolean
      selectionRequired?: boolean
      elements: FormElementDefinition[]
    }
  | {
      kind: 'conditional'
      key: string
      label?: string
      required: boolean
      determinant: FormElementDefinition
      branches: ConditionalBranch[]
      defaultElement?: FormElementDefinition
    }
  | {
      kind: 'list'
      key: string
      label?: string
      required: boolean
      element: FormElementDefinition
      addLabel?: string
      removeLabel?: string
    }
  | { kind: 'section'; key: string; title: string; element: FormElementDefinition }
  | { kind: 'paragraph'; key: string; text: string }
  | { kind: 'unsupported'; key: string; label: string; detail?: string }

type FormPageDefinition = {
  title: string
  key: string
  nextPageKey?: string
  elements: FormElementDefinition[]
}

type FormValue = string | number | boolean | null | EntityOption | LocationValue | FormValues | FormValues[]

interface FormValues {
  [key: string]: FormValue
}

type LocationValue = {
  latitude: number
  longitude: number
  accuracy?: number
}

type FieldErrorMap = Record<string, string>

type EntityOptionsByKey = Map<string, EntityOption[]>

type EntityLoadingByKey = Map<string, boolean>

type EntityErrorByKey = Map<string, string>

type ValueSetter = (key: string, value: FormValue) => void

const LOCATION_PLACEHOLDER = "Tap 'Refresh' to get location"
const DATE_PLACEHOLDER = 'Date'
const DEFAULT_ENTITY_PLACEHOLDER = 'Select an entity'
const DEFAULT_GRADE_PROMPT = 'Select the grade'
const DEFAULT_ADD_LABEL = 'Add field'
const DEFAULT_REMOVE_LABEL = 'Remove'
const MULTI_CHOICE_REQUIRED_MESSAGE = 'At least one box needs to be checked'
const ENTITY_PAGE_SIZE = 200

const DEFAULT_LOCATION: LocationValue = {
  latitude: 0,
  longitude: 0,
  accuracy: 0,
}

const gradeOptionLabels = ['PP1', 'PP2', 'Mixed PP1 & PP2', 'Grade 1', 'Grade 2']

const buildFieldPath = (prefix: string | null, key: string) => (prefix ? `${prefix}.${key}` : key)

const buildListPrefix = (prefix: string | null, key: string, index: number) =>
  prefix ? `${prefix}.${key}[${index}]` : `${key}[${index}]`

const escapePathSelector = (value: string) => {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value)
  return value.replace(/"/g, '\\"')
}

const toJsArray = <T,>(list: kotlin.collections.KtList<T> | undefined | null): readonly T[] =>
  list ? list.asJsReadonlyArrayView() : []

const normalizeString = (value: string | null | undefined) => (value ?? '').trim()

const optionValue = (option: Option) => normalizeString(option.response ?? option.text)

const mapItems = (items: readonly Item[]): FormElementDefinition[] =>
  items.flatMap((item, index) => mapItem(item, index))

const mapItem = (item: Item, index: number): FormElementDefinition[] => {
  if (item instanceof SingleLine)
    return [
      {
        kind: 'input',
        key: item.key,
        input: {
          kind: 'text',
          key: item.key,
          label: item.prompt,
          required: item.required,
          inputType: item.inputType,
        },
      },
    ]
  if (item instanceof MultiLine)
    return [
      {
        kind: 'input',
        key: item.key,
        input: {
          kind: 'textarea',
          key: item.key,
          label: item.prompt,
          required: item.required,
          inputType: InputType.Text,
        },
      },
    ]
  if (item instanceof Checkbox)
    return [
      {
        kind: 'input',
        key: item.key,
        input: {
          kind: 'checkbox',
          key: item.key,
          label: item.prompt,
          required: item.required,
        },
      },
    ]
  if (item instanceof SingleChoice) return [mapSingleChoice(item)]
  if (item instanceof MultipleChoice) return [mapMultipleChoice(item)]
  if (item instanceof MultiAnswer) return [mapMultiAnswer(item)]
  if (item instanceof Description)
    return [
      {
        kind: 'paragraph',
        key: `paragraph_${index}`,
        text: item.text,
      },
    ]
  if (item instanceof Section) return [mapSection(item)]
  if (item instanceof DateItem)
    return [
      {
        kind: 'input',
        key: item.key,
        input: {
          kind: 'date',
          key: item.key,
          label: item.prompt,
          required: item.required,
          placeholder: DATE_PLACEHOLDER,
          defaultValue: item.useCurrentAsDefault ? new Date().toISOString().slice(0, 10) : undefined,
        },
      },
    ]
  if (item instanceof LocationItem)
    return [
      {
        kind: 'input',
        key: item.key,
        input: {
          kind: 'location',
          key: item.key,
          label: item.prompt,
          required: item.required,
          placeholder: LOCATION_PLACEHOLDER,
        },
      },
    ]
  if (item instanceof GradeDropDown) return [mapGradeDropDown(item)]
  if (item instanceof StreamDropDown) return [mapStreamDropDown(item)]
  if (item instanceof EntityChoice) return [mapEntityChoice(item)]
  if (item instanceof SchoolDropDown || item instanceof UserDropDown)
    return [
      {
        kind: 'unsupported',
        key: item.key,
        label: item.prompt,
        detail: 'This field type is not supported in the console preview.',
      },
    ]

  return [
    {
      kind: 'unsupported',
      key: `unsupported_${index}`,
      label: 'Unsupported field',
      detail: 'This field type is not supported in the console preview.',
    },
  ]
}

const wrapContent = (elements: FormElementDefinition[]): FormElementDefinition => ({
  kind: 'composite',
  key: '__',
  required: false,
  elements,
})

const mapSingleChoice = (item: SingleChoice): FormElementDefinition => {
  const optionsWithItems = toJsArray(item.options).map((option, index) => ({
    id: `option_${index}_${normalizeString(option.text)}`,
    label: option.text,
    value: optionValue(option),
    next: option.next ?? null,
    items: mapItems(toJsArray(option.items)),
  }))

  const options: FormOption[] = optionsWithItems.map((option) => ({
    id: option.id,
    label: option.label,
    value: option.value,
    next: option.next,
  }))

  const branches = optionsWithItems
    .filter((option) => option.items.length > 0)
    .map((option) => ({
      id: `branch_${option.id}`,
      matchValue: option.value,
      element: wrapContent(option.items),
    }))

  const defaultIndex = item.default ?? null
  const defaultValue = defaultIndex ? optionsWithItems[defaultIndex - 1]?.value : undefined

  const input: FormInputDefinition = {
    kind: item.style === SingleChoiceStyle.Radio ? 'radio' : 'dropdown',
    key: item.key,
    label: item.prompt,
    required: item.required,
    options,
    defaultValue,
  }

  return {
    kind: 'conditional',
    key: `__conditional ${item.key}`,
    label: undefined,
    required: item.required,
    determinant: { kind: 'input', key: item.key, input },
    branches,
    defaultElement: undefined,
  }
}

const mapMultipleChoice = (item: MultipleChoice): FormElementDefinition => {
  const elements: FormElementDefinition[] = toJsArray(item.options).map((option) => {
    const checkboxElement: FormElementDefinition = {
      kind: 'input',
      key: option.key,
      input: {
        kind: 'checkbox',
        key: option.key,
        label: option.text,
        required: false,
      },
    }

    const optionElements = mapItems(toJsArray(option.items))
    if (optionElements.length === 0) return checkboxElement

    return {
      kind: 'conditional',
      key: `__conditional ${option.key}`,
      label: undefined,
      required: false,
      determinant: checkboxElement,
      branches: [
        {
          id: `branch_${option.key}`,
          matchValue: true,
          element: wrapContent(optionElements),
        },
      ],
      defaultElement: undefined,
    }
  })

  return {
    kind: 'composite',
    key: item.key,
    label: item.prompt,
    required: item.required,
    selectionRequired: item.required,
    elements,
  }
}

const mapMultiAnswer = (item: MultiAnswer): FormElementDefinition => {
  const elements = mapItems(toJsArray(item.items))
  return {
    kind: 'list',
    key: item.key,
    label: item.prompt,
    required: item.required,
    addLabel: item.addLabel ?? undefined,
    removeLabel: item.removeLabel ?? undefined,
    element: {
      kind: 'composite',
      key: item.key,
      required: item.required,
      elements,
    },
  }
}

const mapSection = (item: Section): FormElementDefinition => {
  const elements = mapItems(toJsArray(item.items))
  return {
    kind: 'section',
    key: item.key,
    title: item.title,
    element: {
      kind: 'composite',
      key: item.key,
      required: false,
      elements,
    },
  }
}

const mapGradeDropDown = (item: GradeDropDown): FormElementDefinition => {
  const definedOptions = toJsArray(item.options)
  const gradeOptions = gradeOptionLabels.map((label) => {
    const match = definedOptions.find((option) => option.text === label)
    return {
      label,
      response: match?.response ?? null,
      items: match?.items ?? KtList.fromJsArray([]),
      next: match?.next ?? null,
    }
  })

  const optionsWithItems = gradeOptions.map((option, index) => ({
    id: `grade_${index}_${option.label}`,
    label: option.label,
    value: normalizeString(option.response ?? option.label),
    next: option.next ?? null,
    items: mapItems(toJsArray(option.items)),
  }))

  const options: FormOption[] = optionsWithItems.map((option) => ({
    id: option.id,
    label: option.label,
    value: option.value,
    next: option.next,
  }))

  const branches = optionsWithItems
    .filter((option) => option.items.length > 0)
    .map((option) => ({
      id: `branch_${option.id}`,
      matchValue: option.value,
      element: wrapContent(option.items),
    }))

  const input: FormInputDefinition = {
    kind: 'dropdown',
    key: item.key,
    label: item.prompt ?? DEFAULT_GRADE_PROMPT,
    required: item.required,
    placeholder: item.placeholder ?? item.unfilledText ?? '',
    unfilledText: item.unfilledText ?? undefined,
    options,
  }

  return {
    kind: 'conditional',
    key: `__conditional ${item.key}`,
    label: undefined,
    required: item.required,
    determinant: { kind: 'input', key: item.key, input },
    branches,
    defaultElement: undefined,
  }
}

const mapStreamDropDown = (item: StreamDropDown): FormElementDefinition => {
  const options: FormOption[] = ['A', 'B', 'C', 'D'].map((label, index) => ({
    id: `stream_${index}_${label}`,
    label,
    value: label,
  }))

  return {
    kind: 'input',
    key: item.key,
    input: {
      kind: 'dropdown',
      key: item.key,
      label: item.prompt,
      required: item.required,
      placeholder: item.placeholder ?? item.unfilledText ?? '',
      unfilledText: item.unfilledText ?? undefined,
      options,
    },
  }
}

const mapEntityChoice = (item: EntityChoice): FormElementDefinition => ({
  kind: 'input',
  key: item.key,
  input: {
    kind: 'entity',
    key: item.key,
    label: item.prompt,
    required: item.required,
    placeholder: DEFAULT_ENTITY_PLACEHOLDER,
    entitySelector: item.entities,
    entityStyle: item.style === SingleChoiceStyle.Radio ? 'radio' : 'dropdown',
  },
})

const mapPages = (pages: readonly Page[]): FormPageDefinition[] =>
  pages.map((page, index) => {
    const key = page.key?.trim() || `page_${index + 1}`
    return {
      title: page.title,
      key,
      nextPageKey: page.nextPage?.key ?? undefined,
      elements: mapItems(toJsArray(page.items)),
    }
  })

const buildInitialValuesForElement = (element: FormElementDefinition, values: FormValues) => {
  switch (element.kind) {
    case 'input': {
      const key = element.input.key
      if (element.input.kind === 'checkbox') {
        if (!(key in values)) values[key] = false
      } else if (element.input.kind === 'date') {
        if (element.input.defaultValue) values[key] = element.input.defaultValue
      } else if (element.input.defaultValue) {
        values[key] = element.input.defaultValue
      }
      break
    }
    case 'composite':
      element.elements.forEach((child) => buildInitialValuesForElement(child, values))
      break
    case 'conditional': {
      buildInitialValuesForElement(element.determinant, values)
      const determinantValue = readInputValue(element.determinant, values)
      const branch = element.branches.find((it) => it.matchValue === determinantValue)
      if (branch) buildInitialValuesForElement(branch.element, values)
      break
    }
    case 'list':
      if (!(element.key in values)) values[element.key] = []
      break
    case 'section':
      buildInitialValuesForElement(element.element, values)
      break
    case 'paragraph':
    case 'unsupported':
      break
    default:
      break
  }
}

const buildInitialValues = (pages: FormPageDefinition[]): Record<string, FormValues> => {
  const valuesByPage: Record<string, FormValues> = {}
  pages.forEach((page) => {
    const values: FormValues = {}
    page.elements.forEach((element) => buildInitialValuesForElement(element, values))
    valuesByPage[page.key] = values
  })
  return valuesByPage
}

const containsKey = (element: FormElementDefinition, key: string): boolean => {
  switch (element.kind) {
    case 'input':
      return element.input.key === key
    case 'composite':
      return element.key === key || element.elements.some((child) => containsKey(child, key))
    case 'conditional':
      return (
        element.key === key ||
        containsKey(element.determinant, key) ||
        element.branches.some((branch) => containsKey(branch.element, key)) ||
        (element.defaultElement ? containsKey(element.defaultElement, key) : false)
      )
    case 'list':
      return element.key === key || containsKey(element.element, key)
    case 'section':
      return containsKey(element.element, key)
    case 'paragraph':
    case 'unsupported':
      return element.key === key
    default:
      return false
  }
}

const indexOfNextContainingKey = (elements: FormElementDefinition[], fromIndex: number, key: string) => {
  for (let i = fromIndex + 1; i < elements.length; i += 1) {
    if (containsKey(elements[i], key)) return i
  }
  return null
}

type RevealDirective = { type: 'next' } | { type: 'none' } | { type: 'jump'; key: string }

const nextElement = (element: FormElementDefinition, values: FormValues): RevealDirective => {
  switch (element.kind) {
    case 'input':
      return nextElementForInput(element.input, values)
    case 'composite': {
      for (const child of element.elements) {
        const directive = nextElement(child, values)
        if (directive.type !== 'next') return directive
      }
      return { type: 'next' }
    }
    case 'conditional':
      return nextElement(element.determinant, values)
    case 'list':
      return nextElement(element.element, values)
    case 'section':
      return nextElement(element.element, values)
    case 'paragraph':
    case 'unsupported':
    default:
      return { type: 'next' }
  }
}

const nextElementForInput = (input: FormInputDefinition, values: FormValues): RevealDirective => {
  if (input.kind !== 'radio' && input.kind !== 'dropdown') return { type: 'next' }
  const options = input.options ?? []
  if (options.length === 0) return { type: 'next' }

  const selectedValue = readInputValue({ kind: 'input', key: input.key, input }, values)
  const effectiveValue = selectedValue ?? input.defaultValue ?? null
  const selectedOption = options.find((option) => option.value === effectiveValue)
  const expectsSelection = options.some((option) => option.next)

  if (selectedOption) {
    const next = selectedOption.next
    if (next instanceof NextItem) return { type: 'jump', key: next.key }
    if (next instanceof NextPage) return { type: 'none' }
    return { type: 'next' }
  }

  return expectsSelection ? { type: 'none' } : { type: 'next' }
}

const visibleElements = (elements: FormElementDefinition[], values: FormValues): FormElementDefinition[] => {
  const result: FormElementDefinition[] = []
  let index = 0
  while (index < elements.length) {
    const element = elements[index]
    result.push(element)
    const directive = nextElement(element, values)
    if (directive.type === 'none') break
    if (directive.type === 'next') {
      index += 1
      continue
    }
    const nextIndex = indexOfNextContainingKey(elements, index, directive.key)
    if (nextIndex === null) break
    index = nextIndex
  }
  return result
}

const readInputValue = (element: FormElementDefinition, values: FormValues): FormValue | null => {
  if (element.kind !== 'input') return null
  return values[element.input.key] ?? null
}

const collectNavigableInputs = (elements: FormElementDefinition[]): FormInputDefinition[] => {
  const inputs: FormInputDefinition[] = []
  elements.forEach((element) => {
    switch (element.kind) {
      case 'input':
        inputs.push(element.input)
        break
      case 'composite':
        inputs.push(...collectNavigableInputs(element.elements))
        break
      case 'conditional': {
        inputs.push(...collectNavigableInputs([element.determinant]))
        element.branches.forEach((branch) => {
          inputs.push(...collectNavigableInputs([branch.element]))
        })
        if (element.defaultElement) inputs.push(...collectNavigableInputs([element.defaultElement]))
        break
      }
      case 'section':
        inputs.push(...collectNavigableInputs([element.element]))
        break
      case 'list':
        inputs.push(...collectNavigableInputs([element.element]))
        break
      default:
        break
    }
  })
  return inputs
}

const resolveNextPageKeyFromItems = (page: FormPageDefinition, values: FormValues): string | null => {
  let nextPageKey: string | null = null
  const inputs = collectNavigableInputs(page.elements)
  inputs.forEach((input) => {
    const selectedValue = values[input.key] ?? input.defaultValue ?? null
    const candidate = resolveNextPageForInput(input, selectedValue)
    if (candidate) nextPageKey = candidate
  })
  return nextPageKey
}

const resolveNextPageForInput = (input: FormInputDefinition, value: FormValue | null): string | null => {
  if (input.kind !== 'radio' && input.kind !== 'dropdown') return null
  const options = input.options ?? []
  const selectedOption = options.find((option) => option.value === value)
  if (!selectedOption) return null
  return selectedOption.next instanceof NextPage ? selectedOption.next.key : null
}

const validateInputValue = (input: FormInputDefinition, value: FormValue | null): string | null => {
  const raw = typeof value === 'string' ? value.trim() : value

  if (input.kind === 'checkbox') {
    return input.required && value !== true ? 'This field is required' : null
  }
  if (input.required && (raw === null || raw === '' || raw === undefined)) return 'This field is required'

  if (!raw || input.kind !== 'text') {
    if (input.kind === 'textarea' && input.required && raw === '') return 'This field is required'
    return null
  }
  if (typeof raw !== 'string') return 'Invalid value'

  const inputType = input.inputType?.name
  if (inputType === InputType.Email.name) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(raw) ? null : 'Invalid email address'
  }
  if (inputType === InputType.Uri.name) {
    try {
      new URL(raw)
      return null
    } catch (_) {
      return 'Invalid URI'
    }
  }
  if (inputType === InputType.PhoneNumber.name) {
    const phoneNumber = PhoneNumber.fromString(raw)
    return phoneNumber && isRealMobilePhoneNumber(phoneNumber) ? null : 'Invalid phone number'
  }
  if (inputType === InputType.Integer.name) {
    return Number.isInteger(Number(raw)) ? null : 'Invalid number'
  }
  if (inputType === InputType.Decimal.name) {
    return Number.isFinite(Number(raw)) ? null : 'Invalid number'
  }
  return null
}

const validateElement = (
  element: FormElementDefinition,
  values: FormValues,
  errors: FieldErrorMap,
  prefix: string | null
) => {
  switch (element.kind) {
    case 'input': {
      const path = buildFieldPath(prefix, element.input.key)
      const value = values[element.input.key] ?? null
      const error =
        element.input.kind === 'entity'
          ? element.input.required && !value
            ? 'This field is required'
            : null
          : validateInputValue(element.input, value)
      if (error) errors[path] = error
      break
    }
    case 'composite': {
      if (element.selectionRequired) {
        const hasSelection = element.elements.some((child) => {
          if (child.kind === 'input' && child.input.kind === 'checkbox') {
            return Boolean(values[child.input.key])
          }
          if (child.kind === 'conditional' && child.determinant.kind === 'input') {
            return Boolean(values[child.determinant.input.key])
          }
          return false
        })
        if (!hasSelection) {
          const path = buildFieldPath(prefix, element.key)
          errors[path] = MULTI_CHOICE_REQUIRED_MESSAGE
        }
      }
      element.elements.forEach((child) => validateElement(child, values, errors, prefix))
      break
    }
    case 'conditional': {
      validateElement(element.determinant, values, errors, prefix)
      const determinantValue = readInputValue(element.determinant, values)
      const branch = element.branches.find((it) => it.matchValue === determinantValue)
      if (branch) validateElement(branch.element, values, errors, prefix)
      break
    }
    case 'list': {
      const listValues = values[element.key]
      const entries = Array.isArray(listValues) ? listValues : []
      if (element.required && entries.length === 0) {
        const path = buildFieldPath(prefix, element.key)
        errors[path] = 'This field is required'
      }
      entries.forEach((entry, index) => {
        if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
          const entryPrefix = buildListPrefix(prefix, element.key, index)
          validateElement(element.element, entry as FormValues, errors, entryPrefix)
        }
      })
      break
    }
    case 'section':
      validateElement(element.element, values, errors, prefix)
      break
    case 'paragraph':
    case 'unsupported':
    default:
      break
  }
}

const validatePage = (page: FormPageDefinition, values: FormValues): FieldErrorMap => {
  const errors: FieldErrorMap = {}
  const visible = visibleElements(page.elements, values)
  visible.forEach((element) => validateElement(element, values, errors, null))
  return errors
}

const getFieldValueForId = (entity: Entity, fieldId: FieldId): FieldValue | null => {
  const entries = entity.valuesByFieldId.asJsReadonlyMapView()
  for (const [id, value] of entries.entries()) {
    if (id.equals(fieldId)) return (value as FieldValue) ?? null
  }
  return null
}

const evaluateSelector = (selector: EntitySelector, entity: Entity, currentUser: Entity | null): boolean => {
  if (EntitySelector.All.equals(selector)) return true
  if (EntitySelector.None.equals(selector)) return false
  if (selector instanceof EntitySelector.OfType) return entity.typeId.equals(selector.typeId)
  if (selector instanceof EntitySelector.FieldValueEquals) {
    const value = getFieldValueForId(entity, selector.fieldId)
    return Boolean(value && value.equals(selector.value))
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserEntityId) {
    if (!currentUser) return false
    const value = getFieldValueForId(entity, selector.fieldId)
    return value instanceof ReferenceValue && value.value.equals(currentUser.id)
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
    if (!currentUser) return false
    const value = getFieldValueForId(entity, selector.fieldId)
    const userValue = getFieldValueForId(currentUser, selector.userFieldId)
    return Boolean(value && userValue && value.equals(userValue))
  }
  if (selector instanceof EntitySelector.And)
    return selector.selectors.asJsReadonlyArrayView().every((it) => evaluateSelector(it, entity, currentUser))
  if (selector instanceof EntitySelector.Or)
    return selector.selectors.asJsReadonlyArrayView().some((it) => evaluateSelector(it, entity, currentUser))
  if (selector instanceof EntitySelector.Not) return !evaluateSelector(selector.selector, entity, currentUser)
  return false
}

const getReferencedTypeIds = (
  selector: EntitySelector,
  typesById: Map<string, EntityType>,
  typeIdsByFieldId: Map<string, string>
): Set<string> => {
  if (EntitySelector.None.equals(selector)) return new Set()
  if (EntitySelector.All.equals(selector)) return new Set(typesById.keys())
  if (selector instanceof EntitySelector.OfType) return new Set([selector.typeId.toString()])
  if (selector instanceof EntitySelector.Not)
    return getReferencedTypeIds(selector.selector, typesById, typeIdsByFieldId)
  if (selector instanceof EntitySelector.And || selector instanceof EntitySelector.Or) {
    const entries = selector.selectors.asJsReadonlyArrayView()
    return entries.reduce<Set<string>>((acc, child) => {
      getReferencedTypeIds(child, typesById, typeIdsByFieldId).forEach((id) => acc.add(id))
      return acc
    }, new Set<string>())
  }
  if (
    selector instanceof EntitySelector.FieldValueEquals ||
    selector instanceof EntitySelector.FieldValueEqualsUserEntityId
  ) {
    const typeId = typeIdsByFieldId.get(selector.fieldId.toString())
    return typeId ? new Set([typeId]) : new Set()
  }
  if (selector instanceof EntitySelector.FieldValueEqualsUserFieldValue) {
    const ids = new Set<string>()
    const first = typeIdsByFieldId.get(selector.fieldId.toString())
    const second = typeIdsByFieldId.get(selector.userFieldId.toString())
    if (first) ids.add(first)
    if (second) ids.add(second)
    return ids
  }
  return new Set()
}

const collectEntityChoiceInputs = (elements: FormElementDefinition[]): FormInputDefinition[] => {
  const inputs: FormInputDefinition[] = []
  elements.forEach((element) => {
    switch (element.kind) {
      case 'input':
        if (element.input.kind === 'entity') inputs.push(element.input)
        break
      case 'composite':
        inputs.push(...collectEntityChoiceInputs(element.elements))
        break
      case 'conditional':
        inputs.push(...collectEntityChoiceInputs([element.determinant]))
        element.branches.forEach((branch) => inputs.push(...collectEntityChoiceInputs([branch.element])))
        if (element.defaultElement) inputs.push(...collectEntityChoiceInputs([element.defaultElement]))
        break
      case 'list':
        inputs.push(...collectEntityChoiceInputs([element.element]))
        break
      case 'section':
        inputs.push(...collectEntityChoiceInputs([element.element]))
        break
      default:
        break
    }
  })
  return inputs
}

const buildEntityFieldMap = (typesById: Map<string, EntityType>) => {
  const map = new Map<string, string>()
  typesById.forEach((type, typeId) => {
    type.fields.asJsReadonlyArrayView().forEach((field) => {
      map.set(field.id.toString(), typeId)
    })
  })
  return map
}

const getEntityLabel = (type: EntityType, entity: Entity): string => {
  try {
    return getPrimaryText(type.label, entity)
  } catch (_) {
    return entity.id.toString()
  }
}

const FormPreview = ({
  content,
  renderData,
  previewWidth,
  previewHeight: _previewHeight,
  reloadToken,
  previewSx,
  layout = 'fixed',
  showLog = true,
  onLog,
}: FormPreviewProps) => {
  const isFillLayout = layout === 'fill'
  const [pages, setPages] = useState<FormPageDefinition[]>([])
  const [parseError, setParseError] = useState<string | null>(null)
  const [valuesByPageKey, setValuesByPageKey] = useState<Record<string, FormValues>>({})
  const [navigationStack, setNavigationStack] = useState<string[]>([])
  const [errorsByPath, setErrorsByPath] = useState<FieldErrorMap>({})
  const [logEntries, setLogEntries] = useState<string[]>([])
  const [entityOptionsByKey, setEntityOptionsByKey] = useState<EntityOptionsByKey>(new Map())
  const [entityLoadingByKey, setEntityLoadingByKey] = useState<EntityLoadingByKey>(new Map())
  const [entityErrorByKey, setEntityErrorByKey] = useState<EntityErrorByKey>(new Map())

  const logRef = useRef<HTMLPreElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [dialogContainer, setDialogContainer] = useState<HTMLElement | null>(null)

  const appendLog = useCallback(
    (message: string) => {
      setLogEntries((prev) => [...prev, message])
      onLog?.(message)
    },
    [onLog]
  )

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [logEntries])

  useEffect(() => {
    const body = containerRef.current?.ownerDocument?.body ?? null
    if (body && body !== dialogContainer) {
      setDialogContainer(body)
    }
  })

  useEffect(() => {
    setLogEntries(['Render log initialized...'])
  }, [reloadToken, content])

  useEffect(() => {
    if (!content?.trim()) {
      setPages([])
      setValuesByPageKey({})
      setNavigationStack([])
      setParseError(null)
      return
    }

    try {
      const parsedPages = decodeFormContentFromYaml(content || '')
      const pageArray = toJsArray(parsedPages)
      const mappedPages = mapPages(pageArray)
      setPages(mappedPages)
      setValuesByPageKey(buildInitialValues(mappedPages))
      setNavigationStack(mappedPages.length > 0 ? [mappedPages[0].key] : [])
      setParseError(null)
      appendLog(`Parsed form with ${mappedPages.length} page(s).`)
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error)
      setParseError(`Failed to parse YAML: ${message}`)
      appendLog(`YAML parse error: ${message}`)
      setPages([])
      setValuesByPageKey({})
      setNavigationStack([])
    }
  }, [content, reloadToken, appendLog])

  const entityInputs = useMemo(() => {
    return pages.flatMap((page) => collectEntityChoiceInputs(page.elements))
  }, [pages])

  useEffect(() => {
    if (entityInputs.length === 0) {
      setEntityOptionsByKey(new Map())
      setEntityErrorByKey(new Map())
      setEntityLoadingByKey(new Map())
      return
    }

    const auth = renderData?.auth
    if (!auth?.accessToken || !auth.organizationId || !auth.entityApiUrl) {
      const errorMap = new Map<string, string>()
      entityInputs.forEach((input) => errorMap.set(input.key, 'Missing authentication context for entities.'))
      setEntityErrorByKey(errorMap)
      setEntityOptionsByKey(new Map())
      setEntityLoadingByKey(new Map())
      appendLog('Entity choice error: missing authentication context.')
      return
    }

    const controller = new AbortController()
    const loadingMap = new Map<string, boolean>()
    entityInputs.forEach((input) => loadingMap.set(input.key, true))
    setEntityLoadingByKey(loadingMap)
    setEntityErrorByKey(new Map())

    let cancelled = false

    const load = async () => {
      const typesResponse = await fetch(
        `${auth.entityApiUrl}/organizations/${auth.organizationId}/types?pageIndex=0&pageSize=${ENTITY_PAGE_SIZE}`,
        {
          headers: {
            Authorization: `Bearer ${auth.accessToken}`,
            Accept: 'application/json',
          },
          signal: controller.signal,
        }
      )

      if (!typesResponse.ok) throw new Error(`Failed to load entity types (${typesResponse.status})`)
      const typesText = await typesResponse.text()
      const parsedTypes = entityTypePaginatedResponseFromJsonString(typesText)
      const typeArray = parsedTypes.documents.asJsReadonlyArrayView()
      const typesById = new Map<string, EntityType>()
      typeArray.forEach((type) => typesById.set(type.id.toString(), type))

      const fieldToTypeMap = buildEntityFieldMap(typesById)

      const userEntityId = renderData?.auth?.userEntityId
      let currentUser: Entity | null = null
      if (userEntityId) {
        const encodedIds = encodeURIComponent(userEntityId)
        const userResponse = await fetch(
          `${auth.entityApiUrl}/organizations/${auth.organizationId}/entities?ids=${encodedIds}&related=false&pageIndex=0&pageSize=1`,
          {
            headers: {
              Authorization: `Bearer ${auth.accessToken}`,
              Accept: 'application/json',
            },
            signal: controller.signal,
          }
        )
        if (userResponse.ok) {
          const userText = await userResponse.text()
          const parsedUser = entityPaginatedResponseFromJsonString(userText)
          currentUser = parsedUser.documents.asJsReadonlyArrayView()[0] ?? null
        }
      }

      const referencedTypeIds = new Set<string>()
      entityInputs.forEach((input) => {
        if (!input.entitySelector) return
        getReferencedTypeIds(input.entitySelector, typesById, fieldToTypeMap).forEach((id) => referencedTypeIds.add(id))
      })

      const entitiesByType = new Map<string, readonly Entity[]>()
      await Promise.all(
        Array.from(referencedTypeIds).map(async (typeId) => {
          const response = await fetch(
            `${auth.entityApiUrl}/organizations/${auth.organizationId}/types/${typeId}/entities?related=false&pageIndex=0&pageSize=${ENTITY_PAGE_SIZE}`,
            {
              headers: {
                Authorization: `Bearer ${auth.accessToken}`,
                Accept: 'application/json',
              },
              signal: controller.signal,
            }
          )
          if (!response.ok) throw new Error(`Failed to load entities for type ${typeId}`)
          const text = await response.text()
          const parsedEntities = entityPaginatedResponseFromJsonString(text)
          entitiesByType.set(typeId, parsedEntities.documents.asJsReadonlyArrayView())
        })
      )

      if (cancelled) return

      const optionsByKey = new Map<string, EntityOption[]>()
      const errorByKey = new Map<string, string>()

      entityInputs.forEach((input) => {
        if (!input.entitySelector) return
        if (!currentUser) {
          errorByKey.set(input.key, 'No current user available for entity filtering.')
          optionsByKey.set(input.key, [])
          return
        }

        const typeIds = getReferencedTypeIds(input.entitySelector, typesById, fieldToTypeMap)
        const options: EntityOption[] = []
        typeIds.forEach((typeId) => {
          const type = typesById.get(typeId)
          const entities = entitiesByType.get(typeId) ?? []
          if (!type) return
          entities.forEach((entity) => {
            if (evaluateSelector(input.entitySelector as EntitySelector, entity, currentUser)) {
              options.push({
                id: entity.id.toString(),
                typeId,
                label: getEntityLabel(type, entity),
              })
            }
          })
        })

        if (options.length === 0) {
          errorByKey.set(input.key, 'No entities matched the selector.')
        }
        optionsByKey.set(input.key, options)
      })

      setEntityOptionsByKey(optionsByKey)
      setEntityErrorByKey(errorByKey)
      setEntityLoadingByKey(new Map())
      appendLog(`Loaded entities for ${optionsByKey.size} entity choice field(s).`)
    }

    load().catch((error) => {
      if (cancelled) return
      const message = error instanceof Error ? error.message : String(error)
      const errorMap = new Map<string, string>()
      entityInputs.forEach((input) => errorMap.set(input.key, message))
      setEntityErrorByKey(errorMap)
      setEntityOptionsByKey(new Map())
      setEntityLoadingByKey(new Map())
      appendLog(`Entity choice error: ${message}`)
    })

    return () => {
      cancelled = true
      controller.abort()
    }
  }, [
    appendLog,
    entityInputs,
    renderData?.auth?.accessToken,
    renderData?.auth?.entityApiUrl,
    renderData?.auth?.organizationId,
    renderData?.auth?.userEntityId,
    reloadToken,
  ])

  const currentPageKey = navigationStack[navigationStack.length - 1]
  const currentPage = pages.find((page) => page.key === currentPageKey) ?? null
  const currentValues = currentPage ? valuesByPageKey[currentPage.key] ?? {} : {}
  const visiblePageElements = useMemo(
    () => (currentPage ? visibleElements(currentPage.elements, currentValues) : []),
    [currentPage, currentValues]
  )

  const setValueForPage = useCallback((pageKey: string, key: string, value: FormValue) => {
    setValuesByPageKey((prev) => {
      const nextPageValues = { ...(prev[pageKey] ?? {}) }
      nextPageValues[key] = value
      return { ...prev, [pageKey]: nextPageValues }
    })
  }, [])

  const setListValueForPage = useCallback((pageKey: string, listKey: string, index: number, value: FormValues) => {
    setValuesByPageKey((prev) => {
      const currentPageValues = { ...(prev[pageKey] ?? {}) }
      const listValues = Array.isArray(currentPageValues[listKey])
        ? ([...(currentPageValues[listKey] as FormValues[])] as FormValues[])
        : []
      listValues[index] = value
      currentPageValues[listKey] = listValues
      return { ...prev, [pageKey]: currentPageValues }
    })
  }, [])

  const removeListValueForPage = useCallback((pageKey: string, listKey: string, index: number) => {
    setValuesByPageKey((prev) => {
      const currentPageValues = { ...(prev[pageKey] ?? {}) }
      const listValues = Array.isArray(currentPageValues[listKey])
        ? ([...(currentPageValues[listKey] as FormValues[])] as FormValues[])
        : []
      listValues.splice(index, 1)
      currentPageValues[listKey] = listValues
      return { ...prev, [pageKey]: currentPageValues }
    })
  }, [])

  const addListValueForPage = useCallback((pageKey: string, listKey: string, element: FormElementDefinition) => {
    setValuesByPageKey((prev) => {
      const currentPageValues = { ...(prev[pageKey] ?? {}) }
      const listValues = Array.isArray(currentPageValues[listKey])
        ? ([...(currentPageValues[listKey] as FormValues[])] as FormValues[])
        : []
      const newEntry: FormValues = {}
      buildInitialValuesForElement(element, newEntry)
      listValues.push(newEntry)
      currentPageValues[listKey] = listValues
      return { ...prev, [pageKey]: currentPageValues }
    })
  }, [])

  const handleSubmitPage = useCallback(() => {
    if (!currentPage) return
    const errors = validatePage(currentPage, currentValues)
    setErrorsByPath(errors)

    const errorPaths = Object.keys(errors)
    if (errorPaths.length > 0) {
      const first = errorPaths[0]
      appendLog(`Validation failed on ${first}`)
      requestAnimationFrame(() => {
        const selector = `[data-field-path="${escapePathSelector(first)}"]`
        const target = containerRef.current?.querySelector(selector)
        if (target instanceof HTMLElement) target.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
      return
    }

    const nextFromItems = resolveNextPageKeyFromItems(currentPage, currentValues)
    const nextFromPage = currentPage.nextPageKey
    const currentIndex = pages.findIndex((page) => page.key === currentPage.key)
    const orderedNext = !nextFromItems && !nextFromPage && currentIndex >= 0 ? pages[currentIndex + 1]?.key : null
    const nextKey = nextFromItems ?? nextFromPage ?? orderedNext

    if (nextKey) {
      setNavigationStack((prev) => [...prev, nextKey])
      return
    }

    const submissionPayload = valuesByPageKey
    appendLog('Form submission payload:')
    appendLog(JSON.stringify(submissionPayload, null, 2))
  }, [appendLog, currentPage, currentValues, pages, valuesByPageKey])

  const handlePreviousPage = useCallback(() => {
    setNavigationStack((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev))
  }, [])

  const clearErrorForPath = useCallback((path: string) => {
    setErrorsByPath((prev) => {
      if (!prev[path]) return prev
      const next = { ...prev }
      delete next[path]
      return next
    })
  }, [])

  const setPageValue: ValueSetter = useCallback(
    (key, value) => {
      if (!currentPage) return
      setValueForPage(currentPage.key, key, value)
    },
    [currentPage, setValueForPage]
  )

  const renderInput = (
    input: FormInputDefinition,
    values: FormValues,
    pathPrefix: string | null,
    setValue: ValueSetter
  ) => {
    const fieldPath = buildFieldPath(pathPrefix, input.key)
    const error = errorsByPath[fieldPath]
    const value = values[input.key]

    const label = (
      <Typography sx={{ fontWeight: 600 }}>
        {input.label}
        {input.required ? (
          <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Typography>
        ) : null}
      </Typography>
    )

    if (input.kind === 'checkbox') {
      return (
        <Box data-field-path={fieldPath}>
          <FormControlLabel
            control={
              <MuiCheckbox
                checked={Boolean(value)}
                onChange={(event) => {
                  setValue(input.key, event.target.checked)
                  clearErrorForPath(fieldPath)
                }}
                color="primary"
              />
            }
            label={input.label}
          />
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      )
    }

    if (input.kind === 'radio') {
      return (
        <FormControl data-field-path={fieldPath} error={Boolean(error)} sx={{ gap: 1 }}>
          {label}
          <RadioGroup
            value={typeof value === 'string' ? value : input.defaultValue ?? ''}
            onChange={(event) => {
              setValue(input.key, event.target.value)
              clearErrorForPath(fieldPath)
            }}
          >
            {(input.options ?? []).map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.value}
                control={<Radio color="primary" />}
                label={option.label}
              />
            ))}
          </RadioGroup>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      )
    }

    if (input.kind === 'dropdown') {
      return (
        <FormControl data-field-path={fieldPath} fullWidth error={Boolean(error)}>
          <InputLabel>{input.label}</InputLabel>
          <Select
            label={input.label}
            value={typeof value === 'string' ? value : input.defaultValue ?? ''}
            onChange={(event) => {
              setValue(input.key, event.target.value)
              clearErrorForPath(fieldPath)
            }}
          >
            <MenuItem value="">
              <em>{input.placeholder || input.unfilledText || 'Select'}</em>
            </MenuItem>
            {(input.options ?? []).map((option) => (
              <MenuItem key={option.id} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
      )
    }

    if (input.kind === 'entity') {
      const options = entityOptionsByKey.get(input.key) ?? []
      const isLoading = entityLoadingByKey.get(input.key)
      const entityError = entityErrorByKey.get(input.key)
      if (input.entityStyle === 'radio') {
        return (
          <FormControl data-field-path={fieldPath} error={Boolean(error)} sx={{ gap: 1 }}>
            {label}
            <RadioGroup
              value={typeof value === 'string' ? value : ''}
              onChange={(event) => {
                setValue(input.key, event.target.value)
                clearErrorForPath(fieldPath)
              }}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio color="primary" />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {isLoading && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption">Loading entities...</Typography>
              </Stack>
            )}
            {(error || entityError) && <FormHelperText>{error || entityError}</FormHelperText>}
          </FormControl>
        )
      }
      return (
        <FormControl data-field-path={fieldPath} fullWidth error={Boolean(error)}>
          <InputLabel>{input.label}</InputLabel>
          <Select
            label={input.label}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => {
              const selectedId = event.target.value
              const selected = options.find((option) => option.id === selectedId) ?? null
              setValue(input.key, selected ? selected.id : '')
              clearErrorForPath(fieldPath)
            }}
          >
            <MenuItem value="">
              <em>{input.placeholder || 'Select'}</em>
            </MenuItem>
            {options.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {isLoading && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading entities...</Typography>
            </Stack>
          )}
          {(error || entityError) && <FormHelperText>{error || entityError}</FormHelperText>}
        </FormControl>
      )
    }

    if (input.kind === 'date') {
      return (
        <Box data-field-path={fieldPath}>
          {label}
          <TextField
            fullWidth
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => {
              setValue(input.key, event.target.value)
              clearErrorForPath(fieldPath)
            }}
            sx={{ mt: 1 }}
          />
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      )
    }

    if (input.kind === 'location') {
      const location = value as LocationValue | null | undefined
      return (
        <Box data-field-path={fieldPath}>
          {label}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                if (!navigator.geolocation) {
                  setValue(input.key, DEFAULT_LOCATION)
                  appendLog('Location unavailable, using dummy coordinates.')
                  clearErrorForPath(fieldPath)
                  return
                }
                navigator.geolocation.getCurrentPosition(
                  (position) => {
                    setValue(input.key, {
                      latitude: position.coords.latitude,
                      longitude: position.coords.longitude,
                      accuracy: position.coords.accuracy,
                    })
                    clearErrorForPath(fieldPath)
                  },
                  () => {
                    setValue(input.key, DEFAULT_LOCATION)
                    appendLog('Location unavailable, using dummy coordinates.')
                    clearErrorForPath(fieldPath)
                  }
                )
              }}
            >
              Refresh
            </Button>
            <Typography variant="body2" color="text.secondary">
              {location
                ? `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`
                : input.placeholder || LOCATION_PLACEHOLDER}
            </Typography>
          </Stack>
          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Box>
      )
    }

    const inputType = input.inputType?.name
    const htmlType =
      inputType === InputType.Email.name
        ? 'email'
        : inputType === InputType.PhoneNumber.name
          ? 'tel'
          : inputType === InputType.Integer.name || inputType === InputType.Decimal.name
            ? 'number'
            : 'text'

    return (
      <Box data-field-path={fieldPath}>
        <TextField
          fullWidth
          label={input.label}
          value={typeof value === 'string' ? value : ''}
          onChange={(event) => {
            setValue(input.key, event.target.value)
            clearErrorForPath(fieldPath)
          }}
          type={htmlType}
          error={Boolean(error)}
          helperText={error ?? ' '}
          multiline={input.kind === 'textarea'}
          minRows={input.kind === 'textarea' ? 3 : undefined}
        />
      </Box>
    )
  }

  const renderElement = (
    element: FormElementDefinition,
    values: FormValues,
    pageKey: string,
    pathPrefix: string | null,
    setValue: ValueSetter
  ) => {
    switch (element.kind) {
      case 'input':
        return renderInput(element.input, values, pathPrefix, setValue)
      case 'paragraph':
        return <Typography data-field-path={buildFieldPath(pathPrefix, element.key)}>{element.text}</Typography>
      case 'unsupported':
        return (
          <Alert data-field-path={buildFieldPath(pathPrefix, element.key)} severity="warning">
            {element.label}
            {element.detail ? ` - ${element.detail}` : ''}
          </Alert>
        )
      case 'section':
        return (
          <Stack spacing={2} data-field-path={buildFieldPath(pathPrefix, element.key)}>
            <Typography variant="h6">{element.title}</Typography>
            {renderCompositeChildren(element.element, values, pageKey, pathPrefix, setValue)}
          </Stack>
        )
      case 'composite': {
        const path = buildFieldPath(pathPrefix, element.key)
        const error = errorsByPath[path]
        return (
          <Stack spacing={2} data-field-path={path}>
            {element.label && (
              <Typography sx={{ fontWeight: 600 }}>
                {element.label}
                {element.required ? (
                  <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                    *
                  </Typography>
                ) : null}
              </Typography>
            )}
            {renderCompositeChildren(element, values, pageKey, pathPrefix, setValue)}
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
          </Stack>
        )
      }
      case 'conditional': {
        const determinantValue = readInputValue(element.determinant, values)
        const branch = element.branches.find((it) => it.matchValue === determinantValue)
        return (
          <Stack spacing={2} data-field-path={buildFieldPath(pathPrefix, element.key)}>
            {renderElement(element.determinant, values, pageKey, pathPrefix, setValue)}
            {branch ? renderElement(branch.element, values, pageKey, pathPrefix, setValue) : null}
          </Stack>
        )
      }
      case 'list': {
        const listValues = values[element.key]
        const entries = Array.isArray(listValues) ? listValues : []
        const error = errorsByPath[buildFieldPath(pathPrefix, element.key)]
        return (
          <Stack spacing={2} data-field-path={buildFieldPath(pathPrefix, element.key)}>
            {element.label && (
              <Typography sx={{ fontWeight: 600 }}>
                {element.label}
                {element.required ? (
                  <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                    *
                  </Typography>
                ) : null}
              </Typography>
            )}
            {entries.map((entry, index) => (
              <ListEntry
                key={`${element.key}_${index}`}
                index={index}
                pageKey={pageKey}
                element={element}
                onRemove={removeListValueForPage}
                dialogContainer={dialogContainer}
              >
                {renderListEntry(
                  element,
                  entry as FormValues,
                  pageKey,
                  buildListPrefix(pathPrefix, element.key, index),
                  (key, value) => {
                    const nextEntry = { ...(entry as FormValues), [key]: value }
                    setListValueForPage(pageKey, element.key, index, nextEntry)
                  }
                )}
              </ListEntry>
            ))}
            {error && (
              <Typography variant="caption" color="error">
                {error}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={() => addListValueForPage(pageKey, element.key, element.element)}
              sx={{ alignSelf: 'flex-start' }}
            >
              {element.addLabel ?? DEFAULT_ADD_LABEL}
            </Button>
          </Stack>
        )
      }
      default:
        return null
    }
  }

  const renderListEntry = (
    element: FormElementDefinition,
    entry: FormValues,
    pageKey: string,
    pathPrefix: string | null,
    setValue: ValueSetter
  ) => {
    if (element.kind !== 'list') return null
    if (element.element.kind === 'composite') {
      return renderCompositeChildren(element.element, entry, pageKey, pathPrefix, setValue)
    }
    return renderElement(element.element, entry, pageKey, pathPrefix, setValue)
  }

  const renderCompositeChildren = (
    element: FormElementDefinition,
    values: FormValues,
    pageKey: string,
    pathPrefix: string | null,
    setValue: ValueSetter
  ) => {
    if (element.kind !== 'composite') return null
    return (
      <Stack spacing={2}>
        {element.elements.map((child, index) => (
          <Box key={child.key || `${child.kind}_${index}`}>
            {renderElement(child, values, pageKey, pathPrefix, setValue)}
          </Box>
        ))}
      </Stack>
    )
  }

  if (!currentPage && pages.length === 0 && !parseError) {
    return (
      <Box
        sx={
          previewSx
            ? [
                {
                  width: '100%',
                  height: '100%',
                  bgcolor: 'background.paper',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2,
                  boxSizing: 'border-box',
                },
                ...(Array.isArray(previewSx) ? previewSx : [previewSx]),
              ]
            : {
                width: '100%',
                height: '100%',
                bgcolor: 'background.paper',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
                boxSizing: 'border-box',
              }
        }
      >
        <Typography color="text.secondary">No form content available.</Typography>
      </Box>
    )
  }

  const hasPreviousPage = navigationStack.length > 1
  const nextPageKey =
    currentPage &&
    (resolveNextPageKeyFromItems(currentPage, currentValues) ||
      currentPage.nextPageKey ||
      pages[pages.findIndex((page) => page.key === currentPage.key) + 1]?.key)
  const hasNextPage = Boolean(nextPageKey)

  return (
    <Stack
      spacing={0}
      sx={{
        width: '100%',
        maxWidth: isFillLayout ? 'none' : previewWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        ref={containerRef}
        sx={
          previewSx
            ? [
                {
                  width: '100%',
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  backgroundColor: 'background.paper',
                  p: 2,
                  boxSizing: 'border-box',
                },
                ...(Array.isArray(previewSx) ? previewSx : [previewSx]),
              ]
            : {
                width: '100%',
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                backgroundColor: 'background.paper',
                p: 2,
                boxSizing: 'border-box',
              }
        }
      >
        {parseError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {parseError}
          </Alert>
        )}
        {currentPage && (
          <Stack spacing={3}>
            <Typography variant="h5">{currentPage.title}</Typography>
            {visiblePageElements.map((element, index) => (
              <Box key={element.key || `${element.kind}_${index}`}>
                {renderElement(element, currentValues, currentPage.key, null, setPageValue)}
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          p: 2,
          boxSizing: 'border-box',
        }}
      >
        <Stack direction="row" spacing={2}>
          {hasPreviousPage && (
            <Button variant="outlined" onClick={handlePreviousPage} color="primary" sx={{ flex: 1 }}>
              Previous
            </Button>
          )}
          <Button variant="contained" onClick={handleSubmitPage} color="primary" sx={{ flex: 1 }}>
            {hasNextPage ? 'Next' : 'Submit'}
          </Button>
        </Stack>
      </Box>

      {showLog && (
        <Box
          component="pre"
          ref={logRef}
          sx={{
            flex: 1,
            minHeight: 0,
            fontFamily: 'JetBrains Mono, Fira Code, monospace',
            fontSize: 12,
            lineHeight: 1.4,
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            p: 2,
            boxSizing: 'border-box',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
          }}
        >
          {logEntries.join('\n')}
        </Box>
      )}
    </Stack>
  )
}

type ListEntryProps = {
  index: number
  pageKey: string
  element: Extract<FormElementDefinition, { kind: 'list' }>
  onRemove: (pageKey: string, listKey: string, index: number) => void
  dialogContainer?: HTMLElement | null
  children: ReactNode
}

const ListEntry = ({ index, pageKey, element, onRemove, dialogContainer, children }: ListEntryProps) => {
  const [confirmOpen, setConfirmOpen] = useState(false)
  const removeLabel = element.removeLabel ?? DEFAULT_REMOVE_LABEL

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
      }}
    >
      <Stack spacing={2}>
        {children}
        <Stack direction="row" justifyContent="flex-end">
          <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)}>
            {removeLabel}
          </Button>
        </Stack>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} container={dialogContainer ?? undefined}>
        <DialogTitle>Delete answer?</DialogTitle>
        <DialogContent>Are you sure you want to remove this answer?</DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setConfirmOpen(false)
              onRemove(pageKey, element.key, index)
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default FormPreview
